import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { 
  TrendingUp, Activity, Shield, FileText, Heart, Users, 
  Building2, Calendar, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = {
  primary: '#14b8a6',
  secondary: '#06b6d4',
  warning: '#f59e0b',
  danger: '#ef4444',
  success: '#22c55e',
  purple: '#8b5cf6',
  pink: '#ec4899',
  blue: '#3b82f6'
};

const TrendChart = ({ data, dataKeys, title, type = 'line', yAxisLabel = '%' }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-80 flex items-center justify-center text-muted-foreground">
        <p>No data available. Enter metrics to see trends.</p>
      </div>
    );
  }

  // Transform data for Recharts
  const chartData = data.periods?.map((period, idx) => {
    const point = { period };
    dataKeys.forEach(key => {
      point[key.key] = data[key.key]?.[idx] || 0;
    });
    return point;
  }) || [];

  const colorPalette = [
    CHART_COLORS.primary, CHART_COLORS.secondary, CHART_COLORS.warning,
    CHART_COLORS.danger, CHART_COLORS.success, CHART_COLORS.purple,
    CHART_COLORS.pink, CHART_COLORS.blue
  ];

  return (
    <div className="h-80">
      <ResponsiveContainer width="100%" height={300}>
        {type === 'line' ? (
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="period" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} unit={yAxisLabel} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))', 
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }} 
            />
            <Legend />
            {dataKeys.map((key, idx) => (
              <Line 
                key={key.key}
                type="monotone" 
                dataKey={key.key} 
                name={key.label}
                stroke={colorPalette[idx % colorPalette.length]}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        ) : type === 'area' ? (
          <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="period" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} unit={yAxisLabel} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))', 
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }} 
            />
            <Legend />
            {dataKeys.map((key, idx) => (
              <Area 
                key={key.key}
                type="monotone" 
                dataKey={key.key} 
                name={key.label}
                stroke={colorPalette[idx % colorPalette.length]}
                fill={colorPalette[idx % colorPalette.length]}
                fillOpacity={0.2}
              />
            ))}
          </AreaChart>
        ) : (
          <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="period" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))', 
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }} 
            />
            <Legend />
            {dataKeys.map((key, idx) => (
              <Bar 
                key={key.key}
                dataKey={key.key} 
                name={key.label}
                fill={colorPalette[idx % colorPalette.length]}
              />
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export default function Trends() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [periods, setPeriods] = useState(12);
  const [activeTab, setActiveTab] = useState('clinical');
  const [trendData, setTrendData] = useState({});

  const fetchTrends = async () => {
    setLoading(true);
    try {
      const endpoints = [
        'clinical', 'safety', 'regulatory', 'staff', 'patient-experience', 'program'
      ];
      
      const results = await Promise.all(
        endpoints.map(async (endpoint) => {
          const response = await fetch(`${API_URL}/api/trends/${endpoint}?periods=${periods}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.ok) {
            return { [endpoint.replace('-', '_')]: await response.json() };
          }
          return { [endpoint.replace('-', '_')]: null };
        })
      );
      
      const combined = results.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      setTrendData(combined);
    } catch (error) {
      toast.error('Failed to load trend data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrends();
  }, [token, periods]);

  const TREND_CONFIGS = {
    clinical: {
      icon: Activity,
      title: 'Clinical Quality Trends',
      color: 'text-emerald-500',
      charts: [
        {
          title: 'Symptom Management',
          dataKeys: [
            { key: 'pain_controlled', label: 'Pain Controlled 48h (%)' },
            { key: 'dyspnea_improvement', label: 'Dyspnea Improvement (%)' }
          ],
          type: 'line'
        },
        {
          title: 'Visit & Care Compliance',
          dataKeys: [
            { key: 'missed_visits', label: 'Missed Visits (%)' },
            { key: 'hope_on_time', label: 'HOPE On Time (%)' },
            { key: 'visits_completed', label: 'Visits Completed (%)' }
          ],
          type: 'area'
        },
        {
          title: 'Safety Indicators',
          dataKeys: [
            { key: 'falls', label: 'Falls' },
            { key: 'infections', label: 'Infections' },
            { key: 'wounds_healing', label: 'Wounds Healing (%)' }
          ],
          type: 'bar',
          yAxisLabel: ''
        }
      ]
    },
    safety: {
      icon: Shield,
      title: 'Safety & Incident Trends',
      color: 'text-orange-500',
      charts: [
        {
          title: 'Fall Incidents',
          dataKeys: [
            { key: 'falls_with_injury', label: 'Falls with Injury' },
            { key: 'falls_without_injury', label: 'Falls without Injury' }
          ],
          type: 'bar',
          yAxisLabel: ''
        },
        {
          title: 'Medication Errors',
          dataKeys: [
            { key: 'med_errors_harm', label: 'Med Errors (Harm)' },
            { key: 'med_errors_near_miss', label: 'Med Errors (Near Miss)' }
          ],
          type: 'bar',
          yAxisLabel: ''
        },
        {
          title: 'Healthcare Utilization',
          dataKeys: [
            { key: 'hospitalizations', label: 'Hospitalizations' },
            { key: 'er_visits', label: 'ER Visits' }
          ],
          type: 'line',
          yAxisLabel: ''
        }
      ]
    },
    regulatory: {
      icon: FileText,
      title: 'Regulatory Compliance Trends',
      color: 'text-purple-500',
      charts: [
        {
          title: 'Documentation Timeliness',
          dataKeys: [
            { key: 'cti_timeliness', label: 'CTI Timeliness (%)' },
            { key: 'f2f_window', label: 'F2F Window (%)' },
            { key: 'poc_updates', label: 'POC Updates (%)' }
          ],
          type: 'line'
        },
        {
          title: 'Quality Indicators',
          dataKeys: [
            { key: 'orders_signed', label: 'Orders Signed (%)' },
            { key: 'doc_completeness', label: 'Doc Completeness (%)' }
          ],
          type: 'area'
        },
        {
          title: 'ADR Outcomes',
          dataKeys: [
            { key: 'adr_favorable', label: 'ADR Favorable' },
            { key: 'adr_unfavorable', label: 'ADR Unfavorable' }
          ],
          type: 'bar',
          yAxisLabel: ''
        }
      ]
    },
    staff: {
      icon: Users,
      title: 'Staff Health Trends',
      color: 'text-blue-500',
      charts: [
        {
          title: 'Turnover Rates',
          dataKeys: [
            { key: 'rn_turnover', label: 'RN Turnover (%)' },
            { key: 'cna_turnover', label: 'CNA Turnover (%)' }
          ],
          type: 'line'
        },
        {
          title: 'Staffing Levels',
          dataKeys: [
            { key: 'rn_vacancies', label: 'RN Vacancies' },
            { key: 'overtime_hours', label: 'Overtime Hours' }
          ],
          type: 'bar',
          yAxisLabel: ''
        },
        {
          title: 'Training & Competency',
          dataKeys: [
            { key: 'education_completion', label: 'Education Completion (%)' },
            { key: 'competency_review', label: 'Competency Review (%)' }
          ],
          type: 'area'
        }
      ]
    },
    patient_experience: {
      icon: Heart,
      title: 'Patient Experience Trends',
      color: 'text-red-500',
      charts: [
        {
          title: 'CAHPS Scores',
          dataKeys: [
            { key: 'cahps_communication', label: 'Communication' },
            { key: 'cahps_pain_symptom', label: 'Pain/Symptom Help' },
            { key: 'cahps_emotional', label: 'Emotional Support' },
            { key: 'cahps_timeliness', label: 'Timeliness' }
          ],
          type: 'line'
        },
        {
          title: 'Complaint Management',
          dataKeys: [
            { key: 'complaints_received', label: 'Complaints Received' },
            { key: 'complaints_resolved', label: 'Complaints Resolved' }
          ],
          type: 'bar',
          yAxisLabel: ''
        }
      ]
    },
    program: {
      icon: Building2,
      title: 'Program Overview Trends',
      color: 'text-cyan-500',
      charts: [
        {
          title: 'Census & Admissions',
          dataKeys: [
            { key: 'adc', label: 'ADC' },
            { key: 'admissions', label: 'Admissions' }
          ],
          type: 'line',
          yAxisLabel: ''
        },
        {
          title: 'Discharge Metrics',
          dataKeys: [
            { key: 'live_discharges', label: 'Live Discharges (%)' },
            { key: 'revocations', label: 'Revocations (%)' }
          ],
          type: 'area'
        },
        {
          title: 'Quality Scores',
          dataKeys: [
            { key: 'alos', label: 'ALOS (days)' },
            { key: 'cahps_overall', label: 'CAHPS Overall' }
          ],
          type: 'line',
          yAxisLabel: ''
        }
      ]
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="trends-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-1 gap-6">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-80" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="trends-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <TrendingUp className="h-8 w-8 text-primary" />
            Historical Trends
          </h1>
          <p className="text-muted-foreground mt-1">Track quality metrics over time</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={periods.toString()} onValueChange={(v) => setPeriods(parseInt(v))}>
            <SelectTrigger className="w-36" data-testid="period-select">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="6">Last 6 periods</SelectItem>
              <SelectItem value="12">Last 12 periods</SelectItem>
              <SelectItem value="24">Last 24 periods</SelectItem>
              <SelectItem value="52">Last 52 periods</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchTrends} data-testid="refresh-trends">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Category Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 lg:grid-cols-6 w-full">
          {Object.entries(TREND_CONFIGS).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <TabsTrigger key={key} value={key} className="gap-2" data-testid={`tab-${key}`}>
                <Icon className={`h-4 w-4 ${config.color}`} />
                <span className="hidden sm:inline">{key.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.entries(TREND_CONFIGS).map(([key, config]) => {
          const Icon = config.icon;
          const data = trendData[key];
          
          return (
            <TabsContent key={key} value={key} className="space-y-6 mt-6">
              <div className="flex items-center gap-2 mb-4">
                <Icon className={`h-6 w-6 ${config.color}`} />
                <h2 className="text-xl font-semibold">{config.title}</h2>
                {data?.periods?.length > 0 && (
                  <Badge variant="secondary">{data.periods.length} periods</Badge>
                )}
              </div>
              
              <div className="grid grid-cols-1 gap-6">
                {config.charts.map((chart, idx) => (
                  <Card key={idx}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">{chart.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <TrendChart 
                        data={data} 
                        dataKeys={chart.dataKeys}
                        title={chart.title}
                        type={chart.type}
                        yAxisLabel={chart.yAxisLabel}
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
